import { Link } from 'react-router-dom'
import HDHLogo from '../components/HDHLogo'
import { Upload, Shield, Settings, BarChart3, Users, DollarSign, User } from 'lucide-react'

export default function HDH() {
  return (
    <>
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) translateX(0px) rotate(0deg); }
            25% { transform: translateY(-20px) translateX(10px) rotate(90deg); }
            50% { transform: translateY(-10px) translateX(-5px) rotate(180deg); }
            75% { transform: translateY(-15px) translateX(8px) rotate(270deg); }
          }
          
          @keyframes float-gentle {
            0%, 100% { 
              transform: translateY(0px) scale(1);
              filter: drop-shadow(0 0 15px rgba(138, 43, 226, 0.3));
            }
            50% { 
              transform: translateY(-8px) scale(1.02);
              filter: drop-shadow(0 0 25px rgba(138, 43, 226, 0.5));
            }
          }
          
          @keyframes glisten {
            0%, 100% { 
              background-position: -200% center;
              opacity: 0.6;
            }
            50% { 
              background-position: 200% center;
              opacity: 1;
            }
          }
          
          @keyframes sparkle {
            0%, 100% { 
              opacity: 0;
              transform: scale(0.8) rotate(0deg);
            }
            25% { 
              opacity: 1;
              transform: scale(1.2) rotate(90deg);
            }
            50% { 
              opacity: 0.8;
              transform: scale(1) rotate(180deg);
            }
            75% { 
              opacity: 1;
              transform: scale(1.1) rotate(270deg);
            }
          }
          
          @keyframes pulse-glow {
            0%, 100% { 
              box-shadow: 0 0 20px rgba(212, 175, 55, 0.4);
              transform: scale(1);
            }
            50% { 
              box-shadow: 0 0 40px rgba(212, 175, 55, 0.8);
              transform: scale(1.05);
            }
          }
        `}</style>
      <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-800 via-gray-700 to-gray-600 text-white relative overflow-hidden" 
               style={{
                 background: `
                   linear-gradient(135deg, #374151 0%, #4B5563 25%, #6B7280 50%, #4B5563 75%, #374151 100%),
                   radial-gradient(circle at 20% 50%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
                   radial-gradient(circle at 80% 20%, rgba(138, 43, 226, 0.1) 0%, transparent 50%),
                   radial-gradient(circle at 40% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)
                 `,
                 backgroundSize: '100% 100%, 800px 800px, 600px 600px, 700px 700px'
               }}>
        {/* Metallic shine effect */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-y-1 animate-pulse"></div>
        </div>
        
        {/* Advanced 3D Audio Visualizer */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Floating 3D Music Notes with Advanced Animations */}
          <div className="absolute top-1/4 left-1/4 w-16 h-16 opacity-30">
            <div className="w-full h-full bg-gradient-to-br from-gold-400 to-gold-600 rounded-full animate-spin" style={{animationDuration: '8s'}}>
              <div className="w-8 h-8 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-gold-600">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
            </div>
          </div>
          
          <div className="absolute top-1/3 right-1/4 w-12 h-12 opacity-25">
            <div className="w-full h-full bg-gradient-to-br from-purple-400 to-purple-600 rounded-full animate-pulse" style={{animationDuration: '3s'}}>
              <div className="w-6 h-6 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 text-purple-600">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
            </div>
          </div>

          <div className="absolute bottom-1/3 left-1/3 w-20 h-20 opacity-20">
            <div className="w-full h-full bg-gradient-to-br from-violet-400 to-violet-600 rounded-full animate-spin" style={{animationDuration: '12s', animationDirection: 'reverse'}}>
              <div className="w-10 h-10 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-violet-600">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
            </div>
          </div>

          <div className="absolute top-2/3 right-1/3 w-14 h-14 opacity-30">
            <div className="w-full h-full bg-gradient-to-br from-gold-400 to-gold-600 rounded-full animate-bounce" style={{animationDuration: '4s'}}>
              <div className="w-7 h-7 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-gold-600">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Blockchain Network Visualization */}
        <div className="absolute inset-0 opacity-20">
          {/* Animated Connection Lines */}
          <svg className="w-full h-full absolute inset-0" style={{zIndex: 1}}>
            <defs>
              <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(212, 175, 55, 0.6)" />
                <stop offset="50%" stopColor="rgba(138, 43, 226, 0.6)" />
                <stop offset="100%" stopColor="rgba(59, 130, 246, 0.6)" />
              </linearGradient>
            </defs>
            <path d="M 25% 25% Q 50% 10% 75% 25%" stroke="url(#connectionGradient)" strokeWidth="2" fill="none" opacity="0.6">
              <animate attributeName="opacity" values="0.2;0.8;0.2" dur="3s" repeatCount="indefinite"/>
            </path>
            <path d="M 75% 25% Q 90% 50% 75% 75%" stroke="url(#connectionGradient)" strokeWidth="2" fill="none" opacity="0.6">
              <animate attributeName="opacity" values="0.8;0.2;0.8" dur="3s" repeatCount="indefinite"/>
            </path>
            <path d="M 75% 75% Q 50% 90% 25% 75%" stroke="url(#connectionGradient)" strokeWidth="2" fill="none" opacity="0.6">
              <animate attributeName="opacity" values="0.2;0.8;0.2" dur="3s" repeatCount="indefinite"/>
            </path>
            <path d="M 25% 75% Q 10% 50% 25% 25%" stroke="url(#connectionGradient)" strokeWidth="2" fill="none" opacity="0.6">
              <animate attributeName="opacity" values="0.8;0.2;0.8" dur="3s" repeatCount="indefinite"/>
            </path>
          </svg>
          
          {/* 3D Animated Nodes */}
          <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full animate-ping shadow-lg shadow-gold-400/50" style={{animationDuration: '2s'}}></div>
          <div className="absolute top-1/2 right-1/4 w-4 h-4 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full animate-ping shadow-lg shadow-purple-400/50" style={{animationDelay: '0.5s', animationDuration: '2s'}}></div>
          <div className="absolute bottom-1/4 left-1/2 w-4 h-4 bg-gradient-to-br from-violet-400 to-violet-600 rounded-full animate-ping shadow-lg shadow-violet-400/50" style={{animationDelay: '1s', animationDuration: '2s'}}></div>
          <div className="absolute top-3/4 right-1/3 w-4 h-4 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full animate-ping shadow-lg shadow-gold-400/50" style={{animationDelay: '1.5s', animationDuration: '2s'}}></div>
          
          {/* Central Hub Node */}
          <div className="absolute top-1/2 left-1/2 w-6 h-6 bg-gradient-to-br from-white to-gray-200 rounded-full transform -translate-x-1/2 -translate-y-1/2 shadow-2xl shadow-gold-400/30 animate-pulse" style={{animationDuration: '4s'}}></div>
        </div>

        {/* Floating Data Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="absolute w-2 h-2 bg-gradient-to-r from-gold-400 to-purple-400 rounded-full opacity-30"
                 style={{
                   left: `${Math.random() * 100}%`,
                   top: `${Math.random() * 100}%`,
                   animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
                   animationDelay: `${Math.random() * 2}s`
                 }}></div>
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative">
          <div className="text-center">
            <div className="relative mb-12">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 to-violet-400/10 blur-2xl animate-pulse"></div>
              
              {/* Glistening overlay */}
              <div className="absolute inset-0 opacity-40 pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-white rounded-full animate-sparkle" style={{animationDelay: '0s', animationDuration: '3s'}}></div>
                <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-gold-400 rounded-full animate-sparkle" style={{animationDelay: '1s', animationDuration: '4s'}}></div>
                <div className="absolute bottom-1/3 left-1/3 w-3.5 h-3.5 bg-white rounded-full animate-sparkle" style={{animationDelay: '2s', animationDuration: '3.5s'}}></div>
                <div className="absolute bottom-1/4 right-1/3 w-3 h-3 bg-gold-400 rounded-full animate-sparkle" style={{animationDelay: '0.5s', animationDuration: '4.5s'}}></div>
                <div className="absolute top-1/2 left-1/2 w-2.5 h-2.5 bg-white rounded-full animate-sparkle" style={{animationDelay: '1.5s', animationDuration: '3s'}}></div>
                <div className="absolute top-1/5 right-1/5 w-2 h-2 bg-gold-400 rounded-full animate-sparkle" style={{animationDelay: '2.5s', animationDuration: '4.2s'}}></div>
                <div className="absolute bottom-1/5 left-1/5 w-2.5 h-2.5 bg-white rounded-full animate-sparkle" style={{animationDelay: '0.8s', animationDuration: '3.8s'}}></div>
              </div>
              
              <h1 className="relative text-7xl md:text-9xl lg:text-[10rem] font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-violet-400 to-purple-500" 
                  style={{ 
                    fontFamily: 'Georgia, serif',
                    textShadow: `
                      0 0 20px rgba(138, 43, 226, 0.4),
                      0 0 40px rgba(138, 43, 226, 0.3),
                      2px 2px 4px rgba(0, 0, 0, 0.2)
                    `,
                    filter: 'drop-shadow(0 0 15px rgba(138, 43, 226, 0.3))',
                    transform: 'translateY(0px)',
                    backgroundImage: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.8) 50%, transparent 70%)',
                    backgroundSize: '200% 100%',
                    backgroundPosition: '-200% center',
                    animation: 'float-gentle 6s ease-in-out infinite, glisten 4s ease-in-out infinite'
                  }}>
                Creator Portal
              </h1>
            </div>
            <p className="text-lg md:text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Advanced creator tools and analytics. Upload, manage, and monetize your content with enterprise-grade security and performance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/hdh/uploads" className="btn-primary bg-gold-500 text-black-950 hover:bg-gold-600 text-lg px-8 py-4 shadow-gold">
                <Upload className="w-5 h-5 mr-2" />
                Upload Content
              </Link>
              <Link to="/" className="btn-secondary border-white text-white hover:bg-white hover:text-purple-600 text-lg px-8 py-4">
                <HDHLogo size="sm" variant="icon" className="mr-2" />
                Back to Hub
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Creator Platform Section */}
      <section className="py-24 relative overflow-hidden"
               style={{
                 background: `
                   linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
                   radial-gradient(circle at 30% 40%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
                   radial-gradient(circle at 70% 60%, rgba(138, 43, 226, 0.1) 0%, transparent 50%),
                   radial-gradient(circle at 50% 80%, rgba(59, 130, 246, 0.08) 0%, transparent 50%)
                 `,
                 backgroundSize: '100% 100%, 900px 900px, 800px 800px, 1000px 1000px'
               }}>
        {/* Metallic shine effect */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent transform -skew-y-1 animate-pulse"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-gradient-to-r from-gold-400/10 to-purple-400/10 blur-2xl animate-pulse"></div>
              <h2 className="relative text-5xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500" 
                  style={{ 
                    fontFamily: 'Georgia, serif',
                    textShadow: `
                      0 0 20px rgba(212, 175, 55, 0.4),
                      0 0 40px rgba(212, 175, 55, 0.3),
                      2px 2px 4px rgba(0, 0, 0, 0.2)
                    `,
                    filter: 'drop-shadow(0 0 15px rgba(212, 175, 55, 0.3))'
                  }}>
                Creator Economy
              </h2>
            </div>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto mb-8">
              Join the next generation of creators earning 90% of their revenue with complete ownership of their content and audience relationships
            </p>
            
            {/* Revenue Stats */}
            <div className="bg-gradient-to-r from-gold-500/20 to-purple-500/20 rounded-2xl p-8 border border-gold-500/30 max-w-4xl mx-auto">
              <div className="grid grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-gold-400 mb-2">90%</div>
                  <div className="text-purple-200">Revenue Share</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-purple-400 mb-2">$2.4M</div>
                  <div className="text-purple-200">Total Paid to Creators</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-violet-400 mb-2">15K+</div>
                  <div className="text-purple-200">Active Creators</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Profile Management */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 group hover:scale-105 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-gold-500/20 to-gold-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <User className="w-8 h-8 text-gold-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Profile Management</h3>
              <p className="text-purple-100 mb-4">
                Create and customize your creator profile with gold star verification and media uploads.
              </p>
              <div className="flex flex-col space-y-3">
                <Link to="/create-profile" className="group relative px-6 py-3 bg-gradient-to-r from-gold-500 to-gold-600 text-black font-bold rounded-xl hover:from-gold-600 hover:to-gold-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-2">
                  <User className="w-4 h-4" />
                  Create Profile
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                </Link>
                <Link to="/u/demo" className="group relative px-6 py-2 bg-white/10 backdrop-blur-sm border border-white/30 text-purple-300 font-medium rounded-lg hover:bg-white/20 hover:border-white/50 transition-all duration-300 flex items-center justify-center gap-2 text-sm">
                  <User className="w-4 h-4" />
                  View Demo Profile
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
                </Link>
              </div>
            </div>

            {/* Content Creation */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 group hover:scale-105 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Upload className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Content Creation</h3>
              <p className="text-purple-100 mb-4">
                Upload, organize, and manage your content library with advanced tools and metadata.
              </p>
              <Link to="/hdh/uploads" className="text-purple-400 hover:text-purple-300 font-medium transition-colors">
                Start Creating →
              </Link>
            </div>

            {/* Monetization */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 group hover:scale-105 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-gold-500/20 to-gold-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <DollarSign className="w-8 h-8 text-gold-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Monetization</h3>
              <p className="text-purple-100 mb-4">
                Multiple revenue streams: subscriptions, tips, pay-per-view, and more.
              </p>
              <Link to="/monetization" className="text-gold-400 hover:text-gold-300 font-medium transition-colors">
                Start Earning →
              </Link>
            </div>

            {/* Blockchain Development */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 group hover:scale-105 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Settings className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Blockchain Development</h3>
              <p className="text-purple-100 mb-4">
                Deploy, test, and manage smart contracts with our integrated development environment.
              </p>
              <Link to="/blockchain-development" className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors">
                Start Building →
              </Link>
            </div>

            {/* Analytics */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 group hover:scale-105 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Analytics</h3>
              <p className="text-purple-100 mb-4">
                Detailed insights into audience behavior and content performance.
              </p>
              <Link to="/hdh/analytics" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
                View Analytics →
              </Link>
            </div>

            {/* Community */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 group hover:scale-105 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Community</h3>
              <p className="text-purple-100 mb-4">
                Build and engage with your audience through messaging and social features.
              </p>
              <Link to="/hdh/community" className="text-green-400 hover:text-green-300 font-medium transition-colors">
                Build Community →
              </Link>
            </div>

            {/* True Ownership */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 group hover:scale-105 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-500/20 to-violet-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-violet-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">True Ownership</h3>
              <p className="text-purple-100 mb-4">
                Own your content and audience relationships with no platform lock-in.
              </p>
              <Link to="/hdh/ownership" className="text-violet-400 hover:text-violet-300 font-medium transition-colors">
                Learn More →
              </Link>
            </div>

            {/* Advanced Features */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 group hover:scale-105 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-gold-500/20 to-gold-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Settings className="w-8 h-8 text-gold-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Advanced Features</h3>
              <p className="text-purple-100 mb-4">
                Enterprise-grade security, CDN delivery, and blockchain-powered features.
              </p>
              <Link to="/hdh/features" className="text-gold-400 hover:text-gold-300 font-medium transition-colors">
                Explore Features →
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* Tech Stack Section */}
      <section className="py-24 relative overflow-hidden"
               style={{
                 background: `
                   linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
                   radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
                   radial-gradient(circle at 75% 75%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
                 `,
                 backgroundSize: '100% 100%, 700px 700px, 800px 800px'
               }}>
        {/* Metallic shine effect */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent transform -skew-y-1 animate-pulse"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-gradient-to-r from-gold-400/10 to-blue-400/10 blur-2xl animate-pulse"></div>
            <h2 className="relative text-5xl md:text-7xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>
              <span className="text-white">Powered by </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500"
                    style={{
                      textShadow: `
                        0 0 20px rgba(212, 175, 55, 0.4),
                        0 0 40px rgba(212, 175, 55, 0.3),
                        2px 2px 4px rgba(0, 0, 0, 0.2)
                      `,
                      filter: 'drop-shadow(0 0 15px rgba(212, 175, 55, 0.3))'
                    }}>Enterprise Tech</span>
            </h2>
          </div>
          <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
            Built on the most advanced blockchain and storage technologies for maximum performance and security
          </p>
          
          {/* Sui Blockchain Badge */}
          <div className="mb-12 flex justify-center">
            <div className="bg-gradient-to-r from-purple-600 to-violet-600 rounded-full px-8 py-4 border-2 border-gold-400/50 shadow-2xl">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-bold text-lg">S</span>
                </div>
                <span className="text-white font-bold text-xl">Powered by Sui Blockchain</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 relative">
            {/* Subtle background pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="w-full h-full bg-gradient-to-br from-purple-500 via-blue-500 to-gold-500"></div>
            </div>
            
            <div className="text-center group hover:scale-110 transition-all duration-300 relative z-10">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-purple-500/40 group-hover:shadow-purple-500/60 border-2 border-gold-400/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
                <span className="text-white font-bold text-2xl relative z-10">S</span>
                <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
              </div>
              <h4 className="font-bold text-white mb-3 text-lg group-hover:text-purple-300 transition-colors">Sui Blockchain</h4>
              <p className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors font-medium">Instant transactions • Zero gas fees</p>
            </div>
            
            <div className="text-center group hover:scale-110 transition-all duration-300 relative z-10">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-blue-500/40 group-hover:shadow-blue-500/60 border-2 border-gold-400/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
                <span className="text-white font-bold text-2xl relative z-10">W</span>
                <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
              </div>
              <h4 className="font-bold text-white mb-3 text-lg group-hover:text-blue-300 transition-colors">Walrus Storage</h4>
              <p className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors font-medium">Decentralized storage • IPFS powered</p>
            </div>
            
            <div className="text-center group hover:scale-110 transition-all duration-300 relative z-10">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-green-500/40 group-hover:shadow-green-500/60 border-2 border-gold-400/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
                <span className="text-white font-bold text-2xl relative z-10">S</span>
                <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
              </div>
              <h4 className="font-bold text-white mb-3 text-lg group-hover:text-green-300 transition-colors">Seal Encryption</h4>
              <p className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors font-medium">End-to-end security • Military grade</p>
            </div>
            
            <div className="text-center group hover:scale-110 transition-all duration-300 relative z-10">
              <div className="w-20 h-20 bg-gradient-to-br from-gold-500 to-gold-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-gold-500/40 group-hover:shadow-gold-500/60 border-2 border-purple-400/30 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
                <span className="text-white font-bold text-2xl relative z-10">Q</span>
                <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
              </div>
              <h4 className="font-bold text-white mb-3 text-lg group-hover:text-gold-300 transition-colors">Quilt CDN</h4>
              <p className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors font-medium">Lightning fast delivery • Global network</p>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}
